import { Box, Card, Typography } from "@mui/material"
import React from "react"

import EditBankDetailsDialog from "../../../../dialogs/EditBankDetailsDialog"
import type { BankDetailsFormValues } from "../../../../schemas/BankDetailsSchema"
import { BANK_DETAILS_PLACEHOLDERS } from "../../../../utils/bankDetailsPlaceholders"
import { formatIbanForDisplay } from "../../../../utils/bankingValidation"
import { getPaymentMethodLabel } from "../../../../utils/paymentMethodLabels"
import DefaultPaymentMethodSwitch from "../../../shared/DefaultPaymentMethodSwitch"
import { PaymentTypeMethodActiveToggle } from "../PaymentTypeMethodActiveToggle"

import { type CompanyPaymentTypeMethod, CompanyStatus } from "@/graphql/codegen/graphql"
import { useDialogState } from "@/hooks/useDialogState"
import PaymentActions from "@/screens/CompanySettings/Accounting/components/PaymentTypeDetails/PaymentTypeCard/actions/PaymentActions"
import { usePaymentTermsContext } from "@/screens/CompanySettings/Accounting/contexts/PaymentTermsContext"
import useCompanyPaymentTypes from "@/screens/CompanySettings/Accounting/hooks/useCompanyPaymentTypes"

interface BankDetailsFieldProps {
  method: CompanyPaymentTypeMethod
  onDefaultChange: () => void
  onRefetch?: () => void
  onDelete: (methodIdToDelete?: string) => void
  onToggleActivation: (methodId: string, currentStatus: CompanyStatus) => void
}

const BankDetailsField: React.FC<BankDetailsFieldProps> = ({
  method,
  onDefaultChange,
  onRefetch,
  onDelete,
  onToggleActivation,
}) => {
  const {
    isDialogOpen: isEditBankDetailsOpen,
    openDialog: handleEditBankDetails,
    closeDialog: handleCloseBankDetailsDialog,
  } = useDialogState()
  const { updatePaymentTypeMethod } = useCompanyPaymentTypes({
    companyId: method.companyPaymentTypeId,
    pause: true,
  })
  const { companyStatus } = usePaymentTermsContext()
  const isCompanyActiveStatus = companyStatus === CompanyStatus.Active

  const handleDelete = () => {
    onDelete(method.companyPaymentMethodId)
  }

  const handleSaveBankDetails = async (bankDetails: BankDetailsFormValues) => {
    handleCloseBankDetailsDialog()

    await updatePaymentTypeMethod({
      input: {
        paymentMethodId: method.companyPaymentMethodId,
        iban: bankDetails.iban || null,
        institutionNumber: bankDetails.institutionNumber || null,
        routingNumber: bankDetails.routingNumber || null,
        swiftCode: bankDetails.swiftCode || null,
        accountNumber: bankDetails.accountNumber || null,
      },
    })

    onRefetch?.()
  }

  const handleToggleMethodActivation = () => {
    onToggleActivation(method.companyPaymentMethodId, method.status)
  }

  return (
    <>
      <Card variant='outlined' className='border border-gray-300 bg-white'>
        <Box className='flex gap-6 p-6'>
          <Box className='flex flex-1 flex-col gap-2'>
            <Box className='flex items-center justify-between gap-4'>
              <Typography className='text-ml font-normal text-gray-900'>
                {getPaymentMethodLabel(method.method)} Transaction
              </Typography>

              <Box className='flex items-center gap-4'>
                {isCompanyActiveStatus && (
                  <PaymentTypeMethodActiveToggle
                    onChange={handleToggleMethodActivation}
                    isActive={method.status !== CompanyStatus.Inactive}
                  />
                )}
                <DefaultPaymentMethodSwitch
                  checked={method.isDefault}
                  onChange={onDefaultChange}
                  disabled={method.status !== CompanyStatus.Active}
                />
              </Box>
            </Box>

            <Box className='flex flex-col'>
              <Box className='border-b border-gray-500'></Box>
              <Box className='flex items-center justify-between gap-2 py-4'>
                <Box className='text-sm text-gray-900'>
                  SWIFT:
                  <br />
                  {method.swiftCode || BANK_DETAILS_PLACEHOLDERS.SWIFT_CODE}
                </Box>
                <Box className='text-sm text-gray-900'>
                  IBAN:
                  <br />
                  {method.iban ? formatIbanForDisplay(method.iban) : BANK_DETAILS_PLACEHOLDERS.IBAN}
                </Box>
                <Box className='text-sm text-gray-900'>
                  Transit/Routing Number:
                  <br />
                  {method.routingNumber || BANK_DETAILS_PLACEHOLDERS.ROUTING_NUMBER}
                </Box>
                <Box className='text-sm text-gray-900'>
                  Institution Number:
                  <br />
                  {method.institutionNumber || BANK_DETAILS_PLACEHOLDERS.INSTITUTION_NUMBER}
                </Box>
                <Box className='text-sm text-gray-900'>
                  Account Number:
                  <br />
                  {method.accountNumber || BANK_DETAILS_PLACEHOLDERS.ACCOUNT_NUMBER}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className='flex items-start'>
            <PaymentActions onDelete={handleDelete} onEditBankDetails={handleEditBankDetails} />
          </Box>
        </Box>
      </Card>

      {isEditBankDetailsOpen && (
        <EditBankDetailsDialog
          paymentMethod={method}
          onClose={handleCloseBankDetailsDialog}
          onSave={handleSaveBankDetails}
        />
      )}
    </>
  )
}

export default BankDetailsField
